import debounce from "lodash/debounce";
import { useMemo, useState } from "react";

/**
 * Creates a debounced value with an optional callback that executes when the debounce completes.
 *
 * @param initialValue - Initial value for both immediate and debounced state
 * @param delay - Debounce delay in milliseconds
 * @returns Tuple of [value, setValue, debouncedValue]
 *
 * @example
 * const [search, setSearch] = useDebouncedValue("", 300);
 * const [search, setSearch, debouncedSearch] = useDebouncedValue("", 300);
 *
 * // Update with callback
 * <Input onChange={(e) => setSearch(e.target.value, () => resetPagination())} />
 */
export function useDebouncedValue<T>(initialValue: T, delay = 300) {
  const [value, setValueImmediate] = useState<T>(initialValue);
  const [debouncedValue, setDebouncedValue] = useState<T>(initialValue);

  const debouncedUpdate = useMemo(
    () =>
      debounce((newValue: T, onDebounced?: () => void) => {
        setDebouncedValue(newValue);
        onDebounced?.();
      }, delay),
    [delay],
  );

  const setValue = (newValue: T, onDebounced?: () => void) => {
    setValueImmediate(newValue);
    debouncedUpdate(newValue, onDebounced);
  };

  return [value, setValue, debouncedValue] as const;
}
